import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ClickableText } from 'ClickableText';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The certificate is per-student and not cached offline, so it is an <img>, not StorableImage.
*/

type Props = {
    certificateImageSrc: string;
    isDownloading: boolean;
    allowDownload: boolean;
    onDownload: () => void;

    // The LinkedIn button is still an Angular directive, so the parent passes it in.
    linkedInButton?: React.ReactNode;

    // Mirrors currentUser.canDownloadStreamCertificateOrAddToLinkedInProfile. The LinkedIn button does not check it.
    allowLinkedIn: boolean;

    // The main-column copy is sized by height; the sidebar copy fills the column width.
    placement: 'main-column' | 'sidebar';
    className?: string;
};

export const CertificateBox: FC<Props> = ({
    certificateImageSrc,
    isDownloading,
    allowDownload,
    onDownload,
    linkedInButton,
    allowLinkedIn,
    placement,
    className,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <div className={twMerge('text-center', className)}>
            {/* OFF-SCALE, matching the legacy pixel heights: the spacing scale has no 150px or 275px
                step, and the certificate is an image whose size was tuned to its own artwork. */}
            <img
                src={certificateImageSrc}
                alt=""
                className={
                    placement === 'sidebar'
                        ? 'inline-block h-auto w-full'
                        : 'inline-block h-[150px] w-auto sm:h-[275px]'
                }
            />

            {/* no-top-margin cancels .front-royal-spinner's 100px top margin, which applies here because this is a block. */}
            <div className="flex flex-col items-center">
                {allowDownload && (
                    <ClickableText
                        onPress={onDownload}
                        disabled={isDownloading}
                        color="primary"
                        underline="none"
                        weight="semibold"
                        allowWrap
                        className="text-lg uppercase leading-10"
                    >
                        {isDownloading ? t('signing_certificate') : t('download_certificate')}
                    </ClickableText>
                )}

                {isDownloading ? (
                    <FrontRoyalSpinner className="no-delay no-top-margin" />
                ) : (
                    allowLinkedIn && linkedInButton
                )}
            </div>
        </div>
    );
};
